import CssClassNameVisitor from "../../../../models/reports/reports/CssClassNameVisitor.js";
import HeaderCell from "./HeaderCell.js";
import ReportVisitor from "../../../../models/reports/reports/ReportVisitor.js";

/**
 * Visit a report to render the titles of its columns. The `visit` functions
 * return HeaderCell instance(s) that should be converted later on into
 * renderables (by using `convertHeaderCellsToTableRows` by example).
 *
 * @param {Renderer} itemTitleRenderer - Takes an {@link AbstractColumn}
 *   as argument and should return a title for this item
 * @param {Renderer} budgetNameRenderer - Takes a string representing
 *   the name of a budget as argument and should return a renderable
 *   for it
 * @param {Renderer} dimensionsRenderer - Takes an {@link AbstractPeriodColumn}
 *   as argument and should return a renderable for its dimensions
 * @param {Renderer} estimateNameRenderer - Takes a ForecastEstimate id as
 *   argument and should return a renderable for it. By default, it returns
 *   the id.
 * @param {function(AbstractColumn): string} getColumnCssClasses - Called with a column as argument and should return the CSS
 *   classes to set in the cell of the column title
 */
export default class ColumnTitleRenderer extends ReportVisitor {
	constructor({
		itemTitleRenderer,
		budgetNameRenderer,
		dimensionsRenderer,
		estimateNameRenderer,
		getColumnCssClasses,
	}) {
		super(...arguments);

		this._itemTitleRenderer = itemTitleRenderer;
		this._budgetNameRenderer = budgetNameRenderer;
		this._estimateNameRenderer = estimateNameRenderer;
		this._dimensionsRenderer = dimensionsRenderer;
		this._getColumnCssClasses = getColumnCssClasses;
		this._cssClassNameRenderer = new CssClassNameVisitor();
	}

	visitAbstractColumn(column) {
		return [
			new HeaderCell({
				id: column.getId(),
				renderer:
					({ attributes } = {}) =>
					(html) => {
						html.th(
							{
								klass: this._cssClassNameRenderer.visit(column),
								...attributes,
							},
							this._itemTitleRenderer(column),
						);
					},
			}),
		];
	}

	visitAbstractPeriodColumn(column) {
		return [
			new HeaderCell({
				id: column.getId(),
				renderer:
					({ attributes } = {}) =>
					(html) => {
						html.th(
							{
								klass: this._getColumnCssClasses(column),
								...attributes,
							},
							this._itemTitleRenderer(column),
							this._columnPropertiesRenderer({ column }),
						);
					},
			}),
		];
	}

	/**
	 * Visit a groupColumn. If the group has a title, it will be used as an
	 * umbrella-label for the sub-columns.
	 * If none is provided, the sub-columns will be rendered as if they were
	 * not part of a group.
	 */
	visitGroupColumn(groupColumn) {
		const children = groupColumn.getColumns().reduce((result, column) => {
			result.push(...column.accept(this));
			return result;
		}, []);

		const topCell = new HeaderCell({
			id: groupColumn.getId(),
			children,
			renderer:
				groupColumn.hasTitle() &&
				(({ attributes } = {}) =>
					(html) => {
						html.th(
							{
								klass: this._getColumnCssClasses(groupColumn),
								...attributes,
							},
							groupColumn.getTitle(),
						);
					}),
		});

		return topCell.asNormalizedArray();
	}

	_columnPropertiesRenderer({ column }) {
		return (html) =>
			html.div(
				{ klass: "column-properties" },
				this._columnBudgetRenderer({ column }),
				this._columnForecastEstimateRenderer({ column }),
				this._columnDimensionRenderer({ column }),
			);
	}

	_columnBudgetRenderer({ column }) {
		if (!column.isBudgetColumn()) {
			return this._emptyProperty();
		}

		return this._propertyRenderer({
			property: this._budgetNameRenderer(column.getBudgetName()),
		});
	}

	_columnForecastEstimateRenderer({ column }) {
		if (!column.isForecastEstimateColumn()) {
			return this._emptyProperty();
		}

		return this._propertyRenderer({
			property: this._estimateNameRenderer({
				forecastEstimateId: column.getForecastEstimateId(),
			}),
		});
	}

	_columnDimensionRenderer({ column }) {
		if (!column.hasOwnDimensionSpecification()) {
			return this._emptyProperty();
		}

		return this._propertyRenderer({
			property: this._dimensionsRenderer(column),
		});
	}

	_propertyRenderer({ property }) {
		return (html) => html.span({ klass: "property" }, property);
	}

	_emptyProperty() {
		return () => {};
	}
}
